import { useEffect, useRef, useState } from 'react'
import type { BuildEvent, BuildStatus, ProjectDto } from '@doable/shared-types'
import { Dialog } from './ui/dialog'
import { Badge, type BadgeVariant } from './ui/badge'
import { Button } from './ui/button'

const WS_BASE = import.meta.env.VITE_WS_URL ?? 'ws://localhost:3000'

type LogLine = {
  id: number
  kind: 'step' | 'log' | 'status' | 'completed' | 'failed' | 'started'
  text: string
  at: string
}

type Props = {
  project: ProjectDto
  accessToken: string
  onClose: () => void
  onFinished: (patch: Partial<ProjectDto>) => void
}

export function BuildLogModal({ project, accessToken, onClose, onFinished }: Props) {
  const [lines, setLines] = useState<LogLine[]>([])
  const [status, setStatus] = useState<BuildStatus>('building')
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [terminal, setTerminal] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const pendingCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const idRef = useRef(0)
  const scrollerRef = useRef<HTMLDivElement | null>(null)

  function append(line: Omit<LogLine, 'id'>) {
    idRef.current += 1
    setLines((prev) => [...prev, { ...line, id: idRef.current }])
  }

  useEffect(() => {
    // React 19 StrictMode double-mounts effects in dev. The deferred-close
    // pattern: cleanup schedules the WebSocket close on a 50ms timer; if the
    // next mount happens within that window (StrictMode re-mount), we cancel
    // the close and reuse the same socket. Result: exactly one WS per Build
    // click — and exactly one Claude run + one Anthropic charge.
    if (pendingCloseRef.current) {
      clearTimeout(pendingCloseRef.current)
      pendingCloseRef.current = null
    }

    // Reuse the existing socket if it survived a StrictMode cycle.
    if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) {
      return () => {
        pendingCloseRef.current = setTimeout(() => {
          wsRef.current?.close()
          wsRef.current = null
          pendingCloseRef.current = null
        }, 50)
      }
    }

    let cancelledByCleanup = false
    const url = `${WS_BASE}/v1/projects/${project.id}/build?token=${encodeURIComponent(accessToken)}`
    const ws = new WebSocket(url)
    wsRef.current = ws

    ws.onopen = () => {
      // Control plane waits for the first frame before opening upstream.
      // No prompt → use the project's stored initial_prompt.
      ws.send(JSON.stringify({ type: 'build' }))
    }

    ws.onmessage = (msg) => {
      if (cancelledByCleanup) return
      let event: BuildEvent
      try {
        event = JSON.parse(msg.data) as BuildEvent
      } catch {
        return
      }

      switch (event.type) {
        case 'started':
          append({ kind: 'started', text: 'Build started', at: event.at })
          break
        case 'step':
          append({ kind: 'step', text: event.label, at: event.at })
          break
        case 'log':
          append({ kind: 'log', text: event.message, at: event.at })
          break
        case 'status':
          setStatus(event.status)
          break
        case 'completed':
          setStatus('live')
          setPreviewUrl(event.previewUrl)
          setTerminal(true)
          append({
            kind: 'completed',
            text: `Live at ${event.previewUrl} (${(event.durationMs / 1000).toFixed(1)}s)`,
            at: event.at,
          })
          onFinished({
            status: 'live',
            previewUrl: event.previewUrl,
            lastBuiltAt: event.at,
          })
          break
        case 'failed':
          setStatus('failed')
          setTerminal(true)
          setError(event.reason)
          append({ kind: 'failed', text: `Failed: ${event.reason}`, at: event.at })
          onFinished({ status: 'failed' })
          break
      }
    }

    ws.onerror = () => {
      if (cancelledByCleanup) return
      setError('Connection error')
      setTerminal(true)
    }

    ws.onclose = () => {
      if (cancelledByCleanup) return
      setTerminal(true)
    }

    return () => {
      // Defer the actual close so a StrictMode re-mount within 50 ms reuses
      // the same socket.
      pendingCloseRef.current = setTimeout(() => {
        cancelledByCleanup = true
        try {
          ws.close()
        } catch {
          // ignore
        }
        if (wsRef.current === ws) wsRef.current = null
        pendingCloseRef.current = null
      }, 50)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id])

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight
    }
  }, [lines])

  function cancel() {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'cancel' }))
    }
  }

  const statusVariant: BadgeVariant =
    status === 'live' ? 'success' : status === 'failed' ? 'destructive' : 'volt'

  return (
    <Dialog
      open
      // Overlay/Esc only dismiss once the run is terminal (same rule as before):
      // closing mid-build would orphan the stream. Cancel is in the footer.
      onClose={() => { if (terminal) onClose() }}
      size="lg"
      title={
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Building</span>
          <span className="truncate">{project.name}</span>
          <Badge variant={statusVariant} className="uppercase tracking-wide">{status}</Badge>
        </span>
      }
      description={<span className="font-mono text-xs">{project.stack} · /{project.slug}</span>}
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          {previewUrl ? (
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-success-ink">{previewUrl}</p>
              <p className="text-xs text-muted-foreground">placeholder URL — DNS lands in C7</p>
            </div>
          ) : error ? (
            <p className="truncate text-sm text-destructive">{error}</p>
          ) : (
            <p className="text-sm text-muted-foreground">Streaming events from the worker…</p>
          )}

          {terminal ? (
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          ) : (
            <Button variant="outline" size="sm" onClick={cancel}>Cancel</Button>
          )}
        </div>
      }
    >
      <div
        ref={scrollerRef}
        className="max-h-96 overflow-y-auto rounded-xl bg-console p-4 font-mono text-xs leading-relaxed text-background"
      >
        {lines.length === 0 ? (
          <p className="text-console-foreground/60">Connecting to worker…</p>
        ) : (
          lines.map((l) => (
            <div key={l.id} className="flex gap-3">
              <span className="shrink-0 text-console-foreground/50 tabular-nums">
                {new Date(l.at).toLocaleTimeString([], { hour12: false })}
              </span>
              <span
                className={
                  l.kind === 'step'
                    ? 'font-semibold text-volt-on-dark'
                    : l.kind === 'failed'
                      ? 'text-destructive-soft'
                      : l.kind === 'completed'
                        ? 'text-success-soft'
                        : l.kind === 'started'
                          ? 'text-console-foreground/60'
                          : 'text-console-foreground/85'
                }
              >
                {l.kind === 'step' ? '▸ ' : l.kind === 'log' ? '  ' : ''}
                {l.text}
              </span>
            </div>
          ))
        )}
      </div>
    </Dialog>
  )
}
